import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TableCell from '@material-ui/core/TableCell';
import { Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import WarningIcon from '@material-ui/icons/Warning';
import makeStyles from '@material-ui/styles/makeStyles';
import { connect } from 'react-redux';
import { currencyToSymbol } from 'utils/currencies';
import {
  tomatoColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';

import PlannerSummaryItem from './PlannerSummaryItem';
import menuPlannerUtlis from './utils';

const useStyles = makeStyles({
  tableTd: { padding: '8px' },
  card: {
    maxWidth: '350px',
    margin: '0 auto',
    position: 'relative',
    overflow: 'initial',
  },
  assumptionCard: {
    backgroundColor: tomatoColor,
    color: whiteColor,
    padding: '10px',
    fontWeight: '400',
  },
  warningIcon: {
    fontSize: '1.5em',
    color: tomatoColor,
    marginLeft: '3px',
    verticalAlign: 'middle',
  },
  cardContent: { padding: '16px', fontSize: '0.8rem' },
  span: { fontSize: '14px' },
  tooltip: { cursor: 'help' },
  itemWithNoAssumption: {
    display: 'flex',
    alignItems: 'center',
  },
});

const Assumption = ({
  currentValue,
  expectedValue,
  margin,
  macroName,
  dietName,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  if (currentValue === null || expectedValue === null || margin === null) {
    return null;
  }

  const isValid =
    currentValue <= expectedValue + margin &&
    currentValue >= expectedValue - margin;

  if (isValid) {
    return null;
  }

  return (
    <Tooltip
      className={classes.tooltip}
      title={
        <Card className={classes.assumptionCard}>
          <span className={classes.span}>
            {t(
              `menuPlanner.plannerCardSummary.waring.assumption.${macroName}`,
              { currentValue, expectedValue, margin, dietName }
            )}
          </span>
        </Card>
      }
      placement="right"
    >
      <WarningIcon className={classes.warningIcon} />
    </Tooltip>
  );
};

const PlannerBoxSummary = ({
  assumptionOfCarbohydrates,
  assumptionOfFats,
  assumptionOfProtein,
  assumptionOfRoughage,
  calorific,
  menuPlanners,
  dietName,
  currency,
}) => {
  const { t } = useTranslation();
  const currencySymbol = currencyToSymbol(currency);

  const add = (a, b) => a + b;

  const classes = useStyles();
  const getTotalMenuPlanners = property =>
    menuPlannerUtlis.getTotalMenuPlanners(property, calorific, menuPlanners);

  const getAverageRate = () => {
    let i = 0;
    let containsSizeWithoutRate = false;
    const sum = menuPlanners
      .map(row => {
        const mealTypeId = row.mealType.split('/').pop();
        const key = `${calorific}_${mealTypeId}`;

        if (
          typeof row.sizes === 'undefined' ||
          row.sizes === null ||
          typeof row.sizes[key] === 'undefined' ||
          row.sizes[key] === null
        ) {
          return 0;
        }
        if (
          row.sizes[key].rateCount === null ||
          row.sizes[key].rateCount === 0
        ) {
          containsSizeWithoutRate = true;
        }
        // eslint-disable-next-line no-plusplus -- FIXME
        i++;
        return row.sizes[key].rateCount > 0
          ? row.sizes[key].rate / row.sizes[key].rateCount
          : 0;
      })
      .reduce(add, 0);

    if (containsSizeWithoutRate) {
      return '-';
    }

    return i > 0 ? (sum / i).toFixed(2) : '-';
  };

  const getTotalPercentage = ({ type, constantOfType }) =>
    menuPlannerUtlis.getMacroInPercent({
      type,
      elementConstant: constantOfType,
      calorific,
      menuPlanners,
    });

  const getRoughagePercentage = () => {
    const result = (
      ((getTotalMenuPlanners('roughage') * menuPlannerUtlis.constant.roughage) /
        getTotalMenuPlanners('calories')) *
      100
    ).toFixed(2);

    isNaN(result);
    return isNaN(result) ? 0 : result;
  };

  const getTotalCarbohydratesNet = () =>
    menuPlannerUtlis.getTotalCarbohydratesNet(calorific, menuPlanners);

  const getCarbohydratesNetPercentage = () => {
    const result = (
      ((getTotalCarbohydratesNet() * menuPlannerUtlis.constant.carbohydrates) /
        getTotalMenuPlanners('calories')) *
      100
    ).toFixed(2);

    return isNaN(result) ? 0 : result;
  };

  return (
    <TableCell className={classes.tableTd}>
      {/* eslint-disable-next-line radix -- FIXME */}
      {parseInt(getTotalMenuPlanners('calories')) !== 0 && (
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.calories')}
              value={`${getTotalMenuPlanners('calories')}
                  ${t('menuPlanner.plannerCardSummary.kcal')}`}
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.protein')}
              value={
                <>
                  {getTotalMenuPlanners('protein')}
                  {t('menuPlanner.plannerCardSummary.g')}
                  <Tooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.protein.percentage'
                    )}
                    placement="top"
                  >
                    <span>
                      (
                      {getTotalPercentage({
                        type: 'protein',
                        constantOfType: menuPlannerUtlis.constant.protein,
                      })}
                      %)
                    </span>
                  </Tooltip>
                  <Assumption
                    currentValue={parseFloat(
                      getTotalPercentage({
                        type: 'protein',
                        constantOfType: menuPlannerUtlis.constant.protein,
                      })
                    )}
                    expectedValue={assumptionOfProtein.assumption}
                    margin={assumptionOfProtein.margin}
                    macroName="protein"
                    dietName={dietName}
                    t={t}
                  />
                </>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.carbohydrates')}
              value={
                <>
                  {getTotalMenuPlanners('carbohydrates')}
                  {t('menuPlanner.plannerCardSummary.g')}
                  <Tooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.carbohydrates.percentage'
                    )}
                    placement="top"
                  >
                    <span>
                      (
                      {getTotalPercentage({
                        type: 'carbohydrates',
                        constantOfType: menuPlannerUtlis.constant.carbohydrates,
                      })}
                      %)
                    </span>
                  </Tooltip>
                  <Assumption
                    currentValue={parseFloat(
                      getTotalPercentage({
                        type: 'carbohydrates',
                        constantOfType: menuPlannerUtlis.constant.carbohydrates,
                      })
                    )}
                    expectedValue={assumptionOfCarbohydrates.assumption}
                    margin={assumptionOfCarbohydrates.margin}
                    macroName="carbohydrates"
                    dietName={dietName}
                    t={t}
                  />
                </>
              }
            />
            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.carbohydratesNet')}
              value={
                <>
                  {getTotalCarbohydratesNet()}g
                  <Tooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.carbohydratesNet.percentage'
                    )}
                    placement="top"
                  >
                    <span>
                      ({getCarbohydratesNetPercentage()}
                      %)
                    </span>
                  </Tooltip>
                  <Assumption
                    currentValue={parseFloat(
                      getTotalPercentage({
                        type: 'carbohydrates',
                        constantOfType: menuPlannerUtlis.constant.carbohydrates,
                      })
                    )}
                    expectedValue={assumptionOfCarbohydrates.assumption}
                    margin={assumptionOfCarbohydrates.margin}
                    macroName="carbohydrates"
                    dietName={dietName}
                    t={t}
                  />
                </>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.sugar')}
              value={
                <div className={classes.itemWithNoAssumption}>
                  <div>
                    {getTotalMenuPlanners('sugar')}
                    {t('menuPlanner.plannerCardSummary.g')}
                    <Tooltip
                      title={t(
                        'menuPlanner.plannerCardSummary.sugar.percentage'
                      )}
                      placement="top"
                    >
                      <span>
                        (
                        {getTotalPercentage({
                          type: 'sugar',
                          constantOfType: menuPlannerUtlis.constant.sugar,
                        })}
                        %)
                      </span>
                    </Tooltip>
                  </div>
                  <InfoIconTooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.new.noAssumptionWarning'
                    )}
                  />
                </div>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.roughage')}
              value={
                <>
                  {getTotalMenuPlanners('roughage')}
                  {t('menuPlanner.plannerCardSummary.g')}
                  <Tooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.protein.percentage'
                    )}
                    placement="top"
                  >
                    <span>
                      ({getRoughagePercentage()}
                      %)
                    </span>
                  </Tooltip>
                  <Assumption
                    currentValue={parseFloat(getRoughagePercentage())}
                    expectedValue={assumptionOfRoughage.assumption}
                    margin={assumptionOfRoughage.margin}
                    macroName="roughage"
                    dietName={dietName}
                  />
                </>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.fat')}
              value={
                <>
                  {getTotalMenuPlanners('fat')}g
                  <Tooltip
                    title={t('menuPlanner.plannerCardSummary.fat.percentage')}
                    placement="top"
                  >
                    <span>
                      (
                      {getTotalPercentage({
                        type: 'fat',
                        constantOfType: menuPlannerUtlis.constant.fat,
                      })}
                      %)
                    </span>
                  </Tooltip>
                  <Assumption
                    currentValue={parseFloat(
                      getTotalPercentage({
                        type: 'fat',
                        constantOfType: menuPlannerUtlis.constant.fat,
                      })
                    )}
                    expectedValue={assumptionOfFats.assumption}
                    margin={assumptionOfFats.margin}
                    macroName="fat"
                    dietName={dietName}
                  />
                </>
              }
            />

            <PlannerSummaryItem
              title={t(
                'menuPlanner.plannerCardSummary.new.saturatedFattyAcids'
              )}
              value={
                <div className={classes.itemWithNoAssumption}>
                  <div>
                    {getTotalMenuPlanners('saturatedFattyAcids')}
                    {t('menuPlanner.plannerCardSummary.g')}
                    <Tooltip
                      title={t(
                        'menuPlanner.plannerCardSummary.saturatedFattyAcids.percentage'
                      )}
                      placement="top"
                    >
                      <span>
                        (
                        {getTotalPercentage({
                          type: 'saturatedFattyAcids',
                          constantOfType:
                            menuPlannerUtlis.constant.saturatedFattyAcids,
                        })}
                        %)
                      </span>
                    </Tooltip>
                  </div>
                  <InfoIconTooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.new.noAssumptionWarning'
                    )}
                  />
                </div>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.salt')}
              value={
                <div className={classes.itemWithNoAssumption}>
                  <div>
                    {getTotalMenuPlanners('salt')}
                    {t('menuPlanner.plannerCardSummary.g')}
                    <Tooltip
                      title={t(
                        'menuPlanner.plannerCardSummary.salt.percentage'
                      )}
                      placement="top"
                    >
                      <span>
                        (
                        {getTotalPercentage({
                          type: 'salt',
                          constantOfType: menuPlannerUtlis.constant.salt,
                        })}
                        %)
                      </span>
                    </Tooltip>
                  </div>
                  <InfoIconTooltip
                    title={t(
                      'menuPlanner.plannerCardSummary.new.noAssumptionWarning'
                    )}
                  />
                </div>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.totalWeight')}
              value={
                <>
                  {getTotalMenuPlanners('weightAfterWOM')}
                  {t('menuPlanner.plannerCardSummary.g')} (
                  {getTotalMenuPlanners('weight')}
                  {t('menuPlanner.plannerCardSummary.g')})
                </>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.price')}
              value={
                <>
                  {getTotalMenuPlanners('price')} {currencySymbol}
                </>
              }
            />

            <PlannerSummaryItem
              title={t('menuPlanner.plannerCardSummary.new.rate')}
              value={getAverageRate()}
            />
          </CardContent>
        </Card>
      )}
    </TableCell>
  );
};

const mapStateToProps = state => ({
  currency: state.Brands.brand.multinational.currency,
});

export default connect(mapStateToProps)(PlannerBoxSummary);
